import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FolderKanban, Loader2, Plus, Search, Activity, CheckCircle2, AlertCircle, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const WRITE_ROLES = ["owner", "admin", "researcher"];

const RUN_STATUS_BADGE = {
  queued: { Icon: Clock, cls: "border-slate-200 bg-slate-50 text-slate-700" },
  running: { Icon: Activity, cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  completed: { Icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  failed: { Icon: AlertCircle, cls: "border-rose-200 bg-rose-50 text-rose-700" },
  cancelled: { Icon: X, cls: "border-slate-200 bg-slate-50 text-slate-500" },
};

function RunStatusBadge({ status }) {
  const { t } = useTranslation("projects");
  if (!status) {
    return (
      <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500">
        {t("list.status.none")}
      </Badge>
    );
  }
  const cfg = RUN_STATUS_BADGE[status] || RUN_STATUS_BADGE.queued;
  const Icon = cfg.Icon;
  return (
    <Badge variant="outline" className={cfg.cls}>
      <Icon className="mr-1 h-3 w-3" strokeWidth={2} />
      {t(`list.status.${status}`)}
    </Badge>
  );
}

export default function Projects() {
  const { t } = useTranslation(["projects", "common", "errors"]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [projects, setProjects] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        api.get("/research/projects"),
        api.get("/brands"),
      ]);
      setProjects(pRes.data);
      setBrands(bRes.data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const brandsById = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      brandsById[p.brand_id]?.name?.toLowerCase().includes(q) ||
      p.type.toLowerCase().includes(q)
    );
  }, [projects, query, brandsById]);

  return (
    <div className="space-y-8" data-testid="projects-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <FolderKanban className="mr-1 inline h-3 w-3" strokeWidth={2} />
            EvidenceLab
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
            {t("projects:list.title")}
          </h1>
          <p className="max-w-xl text-sm text-slate-500">{t("projects:list.subtitle")}</p>
        </div>
        {canWrite && (
          <Button
            onClick={() => navigate("/projects/new")}
            data-testid="projects-new-cta"
            className="h-10 bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="mr-1 h-4 w-4" strokeWidth={2} /> {t("projects:list.newProject")}
          </Button>
        )}
      </header>

      <div className="flex items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("projects:list.search")}
            data-testid="projects-search"
            className="h-10 pl-9"
          />
        </div>
        <span className="text-xs text-slate-500" data-testid="projects-count">{filtered.length}</span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="projects-table-container">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
          </div>
        ) : filtered.length === 0 ? (
          projects.length === 0 ? (
            <div className="px-8 py-14 text-center" data-testid="projects-empty">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                <FolderKanban className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">
                {t("projects:list.empty.title")}
              </h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{t("projects:list.empty.body")}</p>
              {canWrite && (
                <div className="mt-6 flex justify-center">
                  <Button
                    onClick={() => navigate("/projects/new")}
                    data-testid="projects-empty-cta"
                    className="bg-slate-900 text-white hover:bg-slate-800"
                  >
                    <Plus className="mr-1 h-4 w-4" strokeWidth={2} />
                    {t("projects:list.empty.cta")}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="px-6 py-10 text-center text-sm text-slate-500">{t("common:search")} — 0</div>
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-slate-200 bg-slate-50">
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("projects:list.columns.name")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("projects:list.columns.brand")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("projects:list.columns.type")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("projects:list.columns.depth")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("projects:list.columns.lastRun")}</TableHead>
                <TableHead className="px-6 py-3 text-[11px] uppercase tracking-wider text-slate-500">{t("projects:list.columns.updated")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.id}
                  data-testid={`project-row-${p.id}`}
                  className="cursor-pointer border-slate-100 transition-colors hover:bg-slate-50"
                  onClick={() => navigate(`/projects/${p.id}`)}
                >
                  <TableCell className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-900">{p.name}</span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-slate-600">
                    {brandsById[p.brand_id]?.name || "—"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-slate-600">{t(`projects:types.${p.type}`)}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-slate-600">{t(`projects:depth.${p.depth}`)}</TableCell>
                  <TableCell className="px-6 py-4">
                    <RunStatusBadge status={p.last_run?.status} />
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs text-slate-500">
                    {new Date(p.updated_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
